import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export interface EventData {
  id: string;
  title: string;
  description?: string;
  event_type: string; // mantido para compatibilidade
  custom_label: string; // nova etiqueta personalizada
  label_color: string; // cor personalizada
  event_date: string;
  duration_minutes: number;
  participants_limit?: number;
  location?: string;
  created_by?: string;
  created_by_profile?: {
    full_name?: string;
    email: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EventsContextType {
  events: EventData[];
  loading: boolean;
  error: string | null;
  
  // CRUD operations
  createEvent: (eventData: Omit<EventData, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'created_by_profile'>) => Promise<{ success: boolean; data?: EventData; error?: string }>;
  updateEvent: (id: string, updates: Partial<EventData>) => Promise<{ success: boolean; data?: EventData; error?: string }>;
  deleteEvent: (id: string) => Promise<{ success: boolean; error?: string }>;
  loadEvents: () => Promise<void>;
  
  // Utility functions
  getEventsForDate: (date: Date) => EventData[];
  getEventsByMonth: (year: number, month: number) => EventData[];
  
  // Debug function
  testConnection: () => Promise<void>;
}

const EventsContext = createContext<EventsContextType | null>(null);

export const useEvents = (): EventsContextType => {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEvents deve ser usado dentro de um EventsProvider');
  }
  return context;
};

interface EventsProviderProps {
  children: ReactNode;
}

export const EventsProvider: React.FC<EventsProviderProps> = ({ children }) => {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, profile } = useAuth();

  // Carregar eventos do banco de dados
  const loadEvents = async (): Promise<void> => {
    try {
      console.log('📋 Carregando eventos...');
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('calendar_events')
        .select(`
          *,
          created_by_profile:user_profiles!calendar_events_created_by_fkey (
            full_name,
            email
          )
        `)
        .eq('is_active', true)
        .order('event_date', { ascending: true });

      if (fetchError) {
        console.error('❌ Erro ao carregar eventos:', fetchError);
        setError('Erro ao carregar eventos: ' + fetchError.message);
        return;
      }

      console.log('✅ Eventos carregados:', data?.length || 0);
      setEvents(data || []);
    } catch (err) {
      console.error('💥 Erro inesperado ao carregar eventos:', err);
      setError('Erro inesperado ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  // Criar novo evento (apenas admins)
  const createEvent = async (
    eventData: Omit<EventData, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'created_by_profile'>
  ): Promise<{ success: boolean; data?: EventData; error?: string }> => {
    try {
      console.log('🔍 Tentando criar evento...');
      console.log('User:', user);
      console.log('Profile:', profile);
      
      if (!user) {
        return { success: false, error: 'Usuário não autenticado' };
      }
      
      if (profile?.role !== 'admin') {
        return { success: false, error: 'Apenas administradores podem criar eventos' };
      }

      const newEvent = {
        ...eventData,
        created_by: user.id,
        is_active: true
      };

      console.log('📝 Dados do evento:', newEvent);

      const { data, error: insertError } = await supabase
        .from('calendar_events')
        .insert([newEvent])
        .select(`
          *,
          created_by_profile:user_profiles!calendar_events_created_by_fkey (
            full_name,
            email
          )
        `)
        .single();

      if (insertError) {
        console.error('❌ Erro ao criar evento:', insertError);
        return { success: false, error: `Erro do banco: ${insertError.message}` };
      }

      console.log('✅ Evento criado com sucesso:', data);

      // Atualizar lista local
      setEvents(prevEvents => [...prevEvents, data].sort((a, b) => 
        new Date(a.event_date).getTime() - new Date(b.event_date).getTime()
      ));

      return { success: true, data };
    } catch (err) {
      console.error('💥 Erro inesperado ao criar evento:', err);
      return { success: false, error: 'Erro inesperado ao criar evento' };
    }
  };

  // Atualizar evento (apenas admins)
  const updateEvent = async (
    id: string, 
    updates: Partial<EventData>
  ): Promise<{ success: boolean; data?: EventData; error?: string }> => {
    try {
      console.log('🔄 Tentando atualizar evento...', id);
      console.log('User:', user);
      console.log('Profile:', profile);
      
      if (!user) {
        return { success: false, error: 'Usuário não autenticado' };
      }
      
      if (profile?.role !== 'admin') {
        return { success: false, error: 'Apenas administradores podem editar eventos' };
      }

      // Remover campos que não devem ser atualizados diretamente
      const { created_by, created_at, created_by_profile, ...validUpdates } = updates;

      console.log('📝 Atualizações:', validUpdates);

      const { data, error: updateError } = await supabase
        .from('calendar_events')
        .update({
          ...validUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select(`
          *,
          created_by_profile:user_profiles!calendar_events_created_by_fkey (
            full_name,
            email
          )
        `)
        .single();

      if (updateError) {
        console.error('❌ Erro ao atualizar evento:', updateError);
        return { success: false, error: `Erro do banco: ${updateError.message}` };
      }

      console.log('✅ Evento atualizado com sucesso:', data);

      // Atualizar lista local
      setEvents(prevEvents => 
        prevEvents.map(event => event.id === id ? data : event)
          .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())
      );

      return { success: true, data };
    } catch (err) {
      console.error('💥 Erro inesperado ao atualizar evento:', err);
      return { success: false, error: 'Erro inesperado ao atualizar evento' };
    }
  };

  // Deletar evento (soft delete - apenas admins)
  const deleteEvent = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('🗑️ === EXCLUSÃO VIA RPC ===');
      console.log('📍 ID do evento:', id);
      
      if (!user) {
        return { success: false, error: 'Usuário não autenticado' };
      }
      
      if (profile?.role !== 'admin') {
        return { success: false, error: 'Apenas administradores podem deletar eventos' };
      }

      // Tentar usar função RPC para soft delete (contorna RLS)
      console.log('🔄 Executando soft delete via RPC...');
      const { data, error: rpcError } = await supabase.rpc('delete_event', {
        event_id: id
      });

      if (rpcError) {
        console.warn('⚠️ RPC delete_event não disponível, tentando método direto...');
        
        // Fallback: tentar UPDATE direto (pode falhar devido ao RLS)
        const { error: updateError } = await supabase
          .from('calendar_events')
          .update({ 
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (updateError) {
          console.error('❌ Erro na exclusão (método direto):', updateError);
          return { success: false, error: `Erro: ${updateError.message}` };
        }
      } else if (data && !data.success) {
        console.error('❌ Erro na exclusão (RPC):', data.message);
        return { success: false, error: data.message };
      }

      console.log('✅ Evento excluído com sucesso!');
      
      // Remover da lista local
      setEvents(prevEvents => prevEvents.filter(event => event.id !== id));

      return { success: true };
    } catch (err) {
      console.error('💥 Erro inesperado:', err);
      return { success: false, error: 'Erro inesperado ao excluir evento' };
    }
  };

  // Obter eventos para uma data específica
  const getEventsForDate = (date: Date): EventData[] => {
    return events.filter(event => {
      const eventDate = new Date(event.event_date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Obter eventos de um mês específico
  const getEventsByMonth = (year: number, month: number): EventData[] => {
    return events.filter(event => {
      const eventDate = new Date(event.event_date);
      return eventDate.getFullYear() === year && eventDate.getMonth() === month;
    });
  };

  // Função de teste de conexão e permissões
  const testConnection = async (): Promise<void> => {
    try {
      console.log('🔧 === TESTE DE CONEXÃO E PERMISSÕES (RLS DESABILITADO) ===');
      
      // 1. Testar autenticação
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('👤 Usuário autenticado:', user?.email || 'Nenhum');
      console.log('🆔 User ID:', user?.id);
      if (userError) console.error('❌ Erro de usuário:', userError);
      
      // 2. Testar sessão
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('🔑 Sessão ativa:', !!session);
      console.log('🕒 Session expires at:', session?.expires_at);
      if (sessionError) console.error('❌ Erro de sessão:', sessionError);
      
      // 3. Testar perfil
      let profile = null;
      if (user) {
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        profile = profileData;
        console.log('👨‍💼 Perfil encontrado:', !!profile);
        console.log('📧 Email do perfil:', profile?.email);
        console.log('👑 Role:', profile?.role);
        console.log('✅ Ativo:', profile?.is_active);
        if (profileError) console.error('❌ Erro de perfil:', profileError);
      }
      
      // 4. Testar leitura de eventos (deve funcionar sem RLS)
      console.log('📖 Testando leitura de eventos...');
      const { data: readEvents, error: readError } = await supabase
        .from('calendar_events')
        .select('id, title, is_active')
        .limit(3);
        
      console.log('📋 Eventos encontrados:', readEvents?.length || 0);
      if (readEvents) {
        readEvents.forEach(event => {
          console.log(`   📄 ${event.title} (ativo: ${event.is_active})`);
        });
      }
      if (readError) console.error('❌ Erro de leitura:', readError);
      
      // 5. Testar escrita (deve funcionar sem RLS)
      console.log('✏️ Testando escrita de eventos (sem RLS)...');
      const testEvent = {
        title: 'Teste de Conexão - RLS OFF',
        description: 'Evento de teste para verificar operações sem RLS',
        event_type: 'test',
        custom_label: 'Teste',
        label_color: '#FF0000',
        event_date: new Date().toISOString(),
        duration_minutes: 30,
        is_active: false,
        created_by: user?.id
      };
      
      const { data: writeTest, error: writeError } = await supabase
        .from('calendar_events')
        .insert([testEvent])
        .select('id, title')
        .single();
        
      if (writeError) {
        console.error('❌ Erro de escrita:', writeError);
      } else {
        console.log('✅ Escrita OK:', writeTest.title);
        
        // Testar UPDATE (que é usado para soft delete)
        console.log('🔄 Testando UPDATE...');
        const { error: updateError } = await supabase
          .from('calendar_events')
          .update({ 
            title: 'Teste UPDATE - OK',
            updated_at: new Date().toISOString()
          })
          .eq('id', writeTest.id);
          
        if (updateError) {
          console.error('❌ Erro de UPDATE:', updateError);
        } else {
          console.log('✅ UPDATE OK!');
        }
        
        // Remover evento de teste completamente
        console.log('🗑️ Removendo teste...');
        const { error: deleteError } = await supabase
          .from('calendar_events')
          .delete()
          .eq('id', writeTest.id);
          
        if (deleteError) {
          console.error('❌ Erro ao remover teste:', deleteError);
        } else {
          console.log('✅ DELETE OK - Teste removido');
        }
      }
      
      console.log('🔧 === FIM DO TESTE (Sem RLS todas as operações devem funcionar) ===');
    } catch (error) {
      console.error('💥 Erro no teste de conexão:', error);
    }
  };

  // Carregar eventos na inicialização
  useEffect(() => {
    loadEvents();
  }, []);

  // Subscrever a mudanças em tempo real
  useEffect(() => {
    const channel = supabase
      .channel('calendar_events_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calendar_events'
        },
        () => {
          // Recarregar eventos quando houver mudanças
          loadEvents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const value: EventsContextType = {
    events,
    loading,
    error,
    createEvent,
    updateEvent,
    deleteEvent,
    loadEvents,
    getEventsForDate,
    getEventsByMonth,
    testConnection
  };

  return (
    <EventsContext.Provider value={value}>
      {children}
    </EventsContext.Provider>
  );
};